import React, { useState } from 'react';
import mbIcon from '../assets/mb_icon.png';
import productBrochure from '../assets/MyLearningBoard-Brochure.pdf';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';

interface ContactUsProps {
  onBack: () => void;
}

// Use existing Firebase app instance (initialized in LoginPage)
const app = getApp();
const db = getFirestore(app);

const ContactUs: React.FC<ContactUsProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'contact' | 'demo'>('contact');
  const [formData, setFormData] = useState({
    contactName: '',
    email: '',
    contactNumber: '',
    schoolName: '',
    role: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.contactName || !formData.email || !formData.contactNumber) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Save to Firestore - this will trigger Cloud Function to send email
      await addDoc(collection(db, 'demo_requests'), {
        contactName: formData.contactName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        schoolName: formData.schoolName || 'Not provided',
        role: formData.role || 'Not specified',
        message: formData.message || 'No message provided',
        timestamp: serverTimestamp(),
        status: 'new'
      });

      alert('Thank you for contacting us! We will get back to you soon.');

      // Clear form
      setFormData({
        contactName: '',
        email: '',
        contactNumber: '',
        schoolName: '',
        role: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error sending your message. Please try again or email us directly at contact@mylearningboard.com');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <img src={mbIcon} alt="My Learning Board" style={{ width: '100px', height: '100px', marginRight: '20px' }} />
              <div>
                <h1 className="mb-2">Contact Us</h1>
                <p className="mb-0">Start Using My Learning Board for your school today!</p>
                <p className="mb-0">Let's have a chat, we would love to hear from you!</p>
              </div>
            </div>
            <button className="btn btn-light" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-5">
        <div className="row g-4">
          {/* Left Section - Resources */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#e8f0f7' }}>
              <div className="card-body text-center py-3">
                <h5 className="mb-3 fw-bold text-dark">Know More About My Learning Board</h5>
                <div className="d-grid gap-2">
                  <a
                    href={productBrochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark"
                  >
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                    Company Brochure
                  </a>
                  <a
                    href="/videos/MLB_demo.mp4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark"
                  >
                    <i className="bi bi-easel me-2"></i>
                    Company Deck
                  </a>
                  <a
                    href={productBrochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark"
                  >
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Product Brochure
                  </a>
                  <a
                    href={productBrochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-dark"
                  >
                    <i className="bi bi-card-checklist me-2"></i>
                    Product Deck
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Tabs */}
          <div className="col-lg-7">
            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-0" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                  type="button"
                  role="tab"
                >
                  <i className="bi bi-envelope me-2"></i>Contact Us
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'demo' ? 'active' : ''}`}
                  onClick={() => setActiveTab('demo')}
                  type="button"
                  role="tab"
                >
                  <i className="bi bi-calendar-check me-2"></i>Book A Demo
                </button>
              </li>
            </ul>

            {/* Contact Us Tab */}
            {activeTab === 'contact' && (
              <div className="card border-0 shadow-sm rounded-top-0">
                <div className="card-body p-4">
                  <h4 className="mb-4 text-dark fw-bold">Contact Us</h4>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Contact Name *"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="email"
                        className="form-control form-control-lg"
                        placeholder="Email Address *"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="tel"
                        className="form-control form-control-lg"
                        placeholder="Contact Number *"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="School/Institute Name"
                        name="schoolName"
                        value={formData.schoolName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="mb-3">
                      <select
                        className="form-select form-select-lg"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Role</option>
                        <option value="principal">Principal</option>
                        <option value="administrator">Administrator</option>
                        <option value="teacher">Teacher</option>
                        <option value="it_head">IT Head</option>
                        <option value="management">Management</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="mb-4">
                      <textarea
                        className="form-control"
                        rows={4}
                        placeholder="Message (Optional)"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary-custom w-100 btn-lg"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Book A Demo Tab */}
            {activeTab === 'demo' && (
              <div className="card border-0 shadow-sm rounded-top-0">
                <div className="card-body p-4">
                  <h4 className="mb-3 text-dark fw-bold">Book A Demo</h4>
                  <p className="text-muted mb-4">Pick a convenient time slot and we'll give you a walkthrough of My Learning Board.</p>
                  <div className="text-center">
                    <a
                      href="https://calendar.app.google/V6NvTPtpmz65BXVZ6"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-calendar2-week text-primary-custom" style={{ fontSize: '120px' }}></i>
                    </a>
                    <p className="text-muted mt-3 mb-4">Select a date & time that works for you</p>
                    <a
                      href="https://calendar.app.google/V6NvTPtpmz65BXVZ6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary-custom btn-lg w-100"
                    >
                      <i className="bi bi-calendar-check me-2"></i>
                      Schedule a Demo Meeting
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information Cards */}
        <div className="row g-4 mt-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-telephone-fill fs-1 text-primary-custom"></i>
                </div>
                <h4 className="mb-3">Phone</h4>
                <p className="mb-2">
                  <strong>Sales:</strong> <a href="tel:+918839393463" className="text-decoration-none">India:(+91) 8839393463</a>
                </p>
                <p className="mb-0">
                  <strong>Support:</strong> <a href="tel:+918839393463" className="text-decoration-none">India:(+91) 8839393463</a>
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-headset fs-1 text-primary-custom"></i>
                </div>
                <h4 className="mb-3">Customer Care</h4>
                <p className="mb-0">
                  <a href="mailto:contact@mylearningboard.com" className="text-decoration-none">
                    contact@mylearningboard.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mt-5">
          <div className="card-body p-0">
            <div className="ratio ratio-21x9">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.3478682724194!2d77.70187317633858!3d12.885338787422185!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae132e85f35b87%3A0xdaa2ef4ff50b8d2f!2sAssetz%2063%20Degree%20East!5e0!3m2!1sen!2sin!4v1762186220543!5m2!1sen!2sin"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Contact Information Footer */}
        <div className="row mt-5">
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100 bg-primary-custom text-white">
              <div className="card-body">
                <h5 className="mb-3">
                  <i className="bi bi-envelope-fill me-2"></i>
                  Email
                </h5>
                <p className="mb-0">contact@mylearningboard.com</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100 bg-primary-custom text-white">
              <div className="card-body">
                <h5 className="mb-3">
                  <i className="bi bi-telephone-fill me-2"></i>
                  Phone
                </h5>
                <p className="mb-0">+91 8839393463</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100 bg-primary-custom text-white">
              <div className="card-body">
                <h5 className="mb-3">
                  <i className="bi bi-clock-fill me-2"></i>
                  Working Hours
                </h5>
                <p className="mb-0">9AM to 9 PM Monday to Saturday</p>
              </div>
            </div>
          </div>
        </div>

        {/* Office Address */}
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-body">
            <h5 className="mb-3">
              <i className="bi bi-building me-2 text-primary-custom"></i>
              Office Address
            </h5>
            <p className="mb-2">
              <strong>Omnix Digital Solutions LLP</strong>
            </p>
            <p className="mb-2">
              <strong>Registered Office:</strong> Survey No. 69/1, 69/2 & 69/3, Hobli, off Sarjapur Rd, Kodathi Village, Varthur, Bengaluru, Karnataka 560035
            </p>
            <p className="mb-0">
              <strong></strong>  {/*CIN No*/}
            </p>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="text-center mt-5">
          <h5 className="mb-4">Connect With Us</h5>
          <div className="d-flex justify-content-center gap-3">
            <a href="#" className="btn btn-outline-primary btn-lg rounded-circle" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-facebook fs-4"></i>
            </a>
            <a href="#" className="btn btn-outline-primary btn-lg rounded-circle" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-instagram fs-4"></i>
            </a>
            <a href="#" className="btn btn-outline-primary btn-lg rounded-circle" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-linkedin fs-4"></i>
            </a>
            <a href="#" className="btn btn-outline-primary btn-lg rounded-circle" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-youtube fs-4"></i>
            </a>
            <a href="#" className="btn btn-outline-primary btn-lg rounded-circle" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-twitter fs-4"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
